import {createColumnHelper} from "@tanstack/react-table";
import {truncateString} from "../../../Utils/StringHelper";
import {PrimaryButton} from "../../Buttons/PrimaryButton";
import {TanStackTable} from "../TanStackTable";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faDownload, faRotate, faPlus} from "@fortawesome/free-solid-svg-icons";
import dayjs from "dayjs";
import {useEffect, useState} from "react";
import {useBoundStore} from "../../../Store/store";
import {ConfirmActionDialog} from "../../Dialogs/Confirmation/ConfirmActionDialog";
import {GenerateContractForShiftDialog} from "../../Dialogs/Contracts/GenerateContractForShiftDialog";
import {useTranslation} from "react-i18next";

export const ContractsTable = (props) => {
    const {
        userId,
        branchId,
        forceNewData = false,
        showGenerateNewButton = false
    } = props;

    const [contracts, setContracts] = useState([]);
    const [metaData, setMetaData] = useState({});
    const [currentPage, setCurrentPage] = useState(1);
    const [loading, setLoading] = useState(true);
    const [showRegenerateModal, setShowRegenerateModal] = useState(false);
    const [regenerateLoading, setRegenerateLoading] = useState(false);
    const [selectedContractId, setSelectedContractId] = useState(null);
    const [showGenerateNewModal, setShowGenerateNewModal] = useState(false);

    const getContractsForUser = useBoundStore((state) => state.getContractsForUser);
    const regenerateContract = useBoundStore((state) => state.regenerateContract);
    const {t} = useTranslation();

    const handleRegenerateClick = (contractId) => {
        setSelectedContractId(contractId);
        setShowRegenerateModal(true);
    };

    const handleRegenerateConfirm = async () => {
        setRegenerateLoading(true);
        const success = await regenerateContract(selectedContractId);
        setRegenerateLoading(false);

        if (success) {
            setShowRegenerateModal(false);
            setSelectedContractId(null);
            const result = await getContractsForUser(userId, currentPage, branchId);
            setContracts(result.data);
            setMetaData(result.meta);
        }
    };

    const handleRegenerateCancel = () => {
        setShowRegenerateModal(false);
        setSelectedContractId(null);
    };

    const handleGenerateNewSuccess = async () => {
        const result = await getContractsForUser(userId, currentPage, branchId);
        setContracts(result.data);
        setMetaData(result.meta);
    };

    useEffect(() => {
        const fetchContracts = async () => {
            setLoading(true);
            const result = await getContractsForUser(userId, currentPage, branchId);
            setContracts(result.data);
            setMetaData(result.meta);
            setLoading(false);
        };

        if (userId || branchId) {
            fetchContracts();
        }
    }, [userId, branchId, currentPage, forceNewData, getContractsForUser]);

    const columnHelper = createColumnHelper()
    const columns = [
        columnHelper.accessor('branch', {
            header: t('tables.customer'),
            cell: props => truncateString(`${props.cell.getValue()?.company_name ?? '-'}`),
        }),
        columnHelper.accessor('freelancer', {
            header: t('tables.name'),
            cell: props => truncateString(`${props.cell.getValue()?.first_name ?? '-'} ${props.cell.getValue()?.last_name ?? ''}`),
        }),
        columnHelper.accessor('id', {
            header: t('tables.contract_number'),
            cell: props => truncateString(props.cell.getValue()),
        }),
        columnHelper.accessor('created_at', {
            header: t('tables.created_at'),
            cell: props => {
                return dayjs.unix(props.cell.getValue().timestamp).format('DD/MM/YYYY HH:mm')
            },
        }),
        columnHelper.accessor('document', {
            header: t('tables.document'),
            cell: props => {
                if((props.cell.getValue() !== null && props.cell.getValue() !== undefined) && props.cell.getValue().hasOwnProperty('url') ) {
                    return (
                        <div className={'flex flex-row space-x-4'}>
                            <FontAwesomeIcon
                                icon={faDownload}
                                className={'text-primary cursor-pointer'}
                                onClick={() => window.open(props.cell.getValue().url, '_blank')}
                                title={t('contracts.download')}
                            />
                            <FontAwesomeIcon
                                icon={faRotate}
                                className={'text-primary cursor-pointer'}
                                onClick={() => handleRegenerateClick(props.row.original.id)}
                                title={t('contracts.regenerate')}
                            />
                        </div>
                    )
                } else {
                    return (
                        <div className={'flex flex-row space-x-4'}>
                            <FontAwesomeIcon
                                icon={faRotate}
                                className={'text-primary cursor-pointer'}
                                onClick={() => handleRegenerateClick(props.row.original.id)}
                                title={t('contracts.regenerate')}
                            />
                        </div>
                    )
                }
            },
        }),
        columnHelper.accessor('actions', {
            header: t('tables.status'),
            cell: props => {
                return (
                    <div className={'flex flex-row space-x-4'}>
                        <div>
                            {
                                ['pending'].includes(props.row.original.status) && (
                                    <div className={'px-2 py-1 bg-orange-300 text-orange-900 rounded-full text-xs opacity-70'}>Te ondertekenen</div>
                                )
                            }

                            {
                                ['sent'].includes(props.row.original.status) && (
                                    <div className={'px-2 py-1 bg-yellow-300 text-yellow-900 rounded-full text-xs opacity-70'}>{t('general.status.sent')}</div>
                                )
                            }

                            {
                                ['completed'].includes(props.row.original.status) && (
                                    <div className={'px-2 py-1 bg-green-300 text-green-900 rounded-full text-xs opacity-70'}>{t('general.status.signed')}</div>
                                )
                            }

                            {
                                ['cancelled'].includes(props.row.original.status) && (
                                    <div className={'px-2 py-1 bg-red-300 text-red-900 rounded-full text-xs opacity-70'}>{t('general.status.cancelled')}</div>
                                )
                            }
                        </div>
                    </div>
                )
            },
        })
    ]

    if (loading) {
        return (
            <div className="flex justify-center items-center py-8">
                <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
            </div>
        );
    }

    if (contracts.length === 0) {
        return (
            <div className="text-center text-gray-500 py-8">
                {t('contracts.no_results')}
            </div>
        );
    }

    return (
        <div>
            {showGenerateNewButton && (
                <div className="mb-4 flex justify-end">
                    <PrimaryButton onClick={() => setShowGenerateNewModal(true)}>
                        <FontAwesomeIcon icon={faPlus} className="mr-2" />
                        Genereer nieuw contract
                    </PrimaryButton>
                </div>
            )}

            <TanStackTable
                columns={columns}
                tableData={contracts}
                forceNewData={forceNewData}
                laravelPaginationMetaData={metaData}
                onChangePage={(page) => setCurrentPage(page + 1)}
            />

            <ConfirmActionDialog
                open={showRegenerateModal}
                onClose={handleRegenerateCancel}
                onConfirm={handleRegenerateConfirm}
                title={t('contracts.regenerate_title')}
                message={t('contracts.regenerate_message')}
                subMessage={t('contracts.regenerate_confirm')}
                loading={regenerateLoading}
            />

            <GenerateContractForShiftDialog
                open={showGenerateNewModal}
                onClose={() => setShowGenerateNewModal(false)}
                onSuccess={handleGenerateNewSuccess}
                userId={userId}
                branchId={branchId}
            />
        </div>
    )
}